/**
 * Admin > Workspaces > Manage: a drawer for one workspace (Details, Members, Invitations) without
 * switching the active workspace. Invitations are listed read-only here: sending and accepting them is
 * done on the workspace's Team page.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  fetchAdminWorkspaceMembers, listInvitations, removeWorkspaceMember, setWorkspaceActive,
  transferWorkspaceOwnership, updateMemberRole, updateWorkspace,
} from '@/services/authApi';
import type { AdminWorkspace } from '@/services/authApi';
import type { WorkspaceMember } from '@/types/auth';
import { apiErrorText } from './apiErrorText';

const LIST_KEY = ['admin', 'workspaces'] as const;
const membersKey = (id: string) => ['admin', 'workspace-members', id] as const;
const PACKAGE_LABEL: Record<string, string> = { seo: 'SEO', ads: 'Ads', both: 'Both' };
const TIER_LABEL: Record<number, string> = { 1: 'DTC', 2: 'B2B', 3: 'Enterprise' };
const CLIENT_STATUS_LABEL: Record<string, string> = { active: 'Active', paused: 'Paused', offboarded: 'Offboarded' };

/** A change response that says the membership cache could not be cleared: the change is saved but can lag. */
type ChangeResult = { cache_invalidated?: boolean } | undefined | null;
type StaleNote = (result: ChangeResult) => void;

function useRefreshAfterChange(workspaceId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: membersKey(workspaceId) });
    queryClient.invalidateQueries({ queryKey: LIST_KEY });
  };
}

function Confirm({
  open, title, text, confirmLabel = 'Confirm', pending, onCancel, onConfirm, children,
}: {
  open: boolean; title: string; text: string; confirmLabel?: string; pending?: boolean;
  onCancel: () => void; onConfirm: () => void; children?: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{text}</DialogDescription>
        </DialogHeader>
        {children}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} disabled={pending}>{confirmLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------

function DetailsTab({ workspace, onResult }: { workspace: AdminWorkspace; onResult: StaleNote }) {
  const refresh = useRefreshAfterChange(workspace.id);
  const [name, setName] = useState(workspace.name);
  const [slug, setSlug] = useState(workspace.slug);
  const [confirmActive, setConfirmActive] = useState(false);

  const changed: { name?: string; slug?: string } = {};
  if (name.trim() !== workspace.name) changed.name = name.trim();
  if (slug.trim() !== workspace.slug) changed.slug = slug.trim();
  const dirty = Object.keys(changed).length > 0;

  const save = useMutation({
    mutationFn: () => updateWorkspace(workspace.id, changed),
    onSuccess: () => { toast.success('Workspace updated'); refresh(); },
    onError: (err: unknown) => toast.error(apiErrorText(err, 'Could not save the workspace')),
  });
  const toggle = useMutation({
    mutationFn: () => setWorkspaceActive(workspace.id, !workspace.is_active),
    onSuccess: (result) => {
      toast.success(workspace.is_active ? 'Workspace deactivated' : 'Workspace reactivated');
      setConfirmActive(false);
      onResult(result);
      refresh();
    },
    onError: (err: unknown) => toast.error(apiErrorText(err, 'Could not change the workspace')),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="ws-name">Workspace name</Label>
        <Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ws-slug">Slug</Label>
        <Input id="ws-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
      </div>
      <div className="space-y-1 text-sm">
        <p className="font-medium">Website</p>
        <p>{workspace.website || '—'}</p>
        <p className="text-xs text-muted-foreground">the website is the client&apos;s ID; changing it needs a migration</p>
      </div>
      <p className="text-sm">{workspace.owner_email ? `Owner: ${workspace.owner_email}` : 'No owner yet'}</p>
      <dl className="grid grid-cols-2 gap-1 text-sm text-muted-foreground">
        <dt>Package</dt><dd>{PACKAGE_LABEL[workspace.product_package ?? ''] ?? '—'}</dd>
        <dt>Tier</dt><dd>{TIER_LABEL[workspace.google_ads_product_tier ?? 0] ?? '—'}</dd>
        <dt>Client status</dt><dd>{CLIENT_STATUS_LABEL[workspace.client_status ?? ''] ?? '—'}</dd>
        <dt>Workspace</dt>
        <dd>{workspace.is_active ? <Badge variant="outline">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</dd>
      </dl>
      <p className="text-xs text-muted-foreground">Package, tier and client status are changed in the table.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>Save changes</Button>
        <Button variant="outline" onClick={() => setConfirmActive(true)}>
          {workspace.is_active ? 'Deactivate workspace' : 'Reactivate workspace'}
        </Button>
      </div>
      <Confirm
        open={confirmActive}
        title={workspace.is_active ? `Deactivate ${workspace.name}?` : `Reactivate ${workspace.name}?`}
        text={workspace.is_active
          ? 'Members lose access to this workspace until it is reactivated. Nothing is deleted.'
          : 'Members get their access back.'}
        pending={toggle.isPending}
        onCancel={() => setConfirmActive(false)}
        onConfirm={() => toggle.mutate()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

function MembersTab({ workspace, onResult }: { workspace: AdminWorkspace; onResult: StaleNote }) {
  const refresh = useRefreshAfterChange(workspace.id);
  const { data, isLoading, isError } = useQuery({
    queryKey: membersKey(workspace.id),
    queryFn: () => fetchAdminWorkspaceMembers(workspace.id),
  });
  const members: WorkspaceMember[] = data ?? [];
  const [removing, setRemoving] = useState<WorkspaceMember | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState('');

  const owner = members.find((m) => m.role === 'owner');
  const seatHolders = members.filter((m) => m.role === 'owner' || m.role === 'admin');
  const candidates = members.filter((m) => m.role !== 'owner' && m.is_active !== false);
  // The only owner/admin seat can neither be demoted nor (while others remain) removed.
  const isLastSeat = (m: WorkspaceMember) =>
    m.role === 'admin' && seatHolders.length === 1 && seatHolders[0].user_id === m.user_id;
  const inactive = members.filter((m) => m.role !== 'owner' && m.is_active === false);

  const role = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: 'admin' | 'editor' | 'viewer' }) =>
      updateMemberRole(workspace.id, userId, next),
    onSuccess: (result) => { toast.success('Role updated'); onResult(result); refresh(); },
    onError: (err: unknown) => toast.error(apiErrorText(err, 'Could not change the role')),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeWorkspaceMember(workspace.id, userId),
    onSuccess: (result) => { toast.success('Member removed'); setRemoving(null); onResult(result); refresh(); },
    onError: (err: unknown) => toast.error(apiErrorText(err, 'Could not remove the member')),
  });
  const transfer = useMutation({
    mutationFn: (userId: string) => transferWorkspaceOwnership(workspace.id, userId),
    onSuccess: (result) => {
      toast.success('Ownership transferred'); setTransferOpen(false); setNewOwnerId(''); onResult(result); refresh();
    },
    onError: (err: unknown) => toast.error(apiErrorText(err, 'Could not transfer ownership')),
  });

  const newOwner = candidates.find((m) => m.user_id === newOwnerId);
  const sentence = newOwner
    ? (owner ? `${newOwner.email} becomes owner, ${owner.email} becomes admin` : `${newOwner.email} becomes owner`)
    : null;

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading members…</p>;
  if (isError) return <p className="text-sm text-destructive">Could not load the members.</p>;

  return (
    <div className="space-y-3">
      {role.isPending && <p id="role-saving" className="text-xs text-muted-foreground" role="status">Saving…</p>}
      <ul className="divide-y divide-border rounded-md border border-border">
        {members.map((m) => {
          const last = isLastSeat(m);
          const lastWithOthers = last && members.length > 1;
          const isOwner = m.role === 'owner';
          const label = m.email ?? m.user_id;
          const reasonId = `reason-${m.user_id}`;
          const removeReason = isOwner ? 'Transfer ownership first' : lastWithOthers ? 'Last owner or admin' : null;
          const roleReason = last ? 'Last owner or admin' : null;
          const selectDescribed = roleReason ? reasonId : role.isPending ? 'role-saving' : undefined;
          return (
            <li key={m.user_id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{label}</p>
                {m.full_name && <p className="truncate text-xs text-muted-foreground">{m.full_name}</p>}
              </div>
              {isOwner ? (
                <span className="text-sm font-medium">Owner</span>
              ) : (
                <Select
                  value={m.role}
                  onValueChange={(next) => role.mutate({ userId: m.user_id, next: next as 'admin' | 'editor' | 'viewer' })}
                  disabled={last || role.isPending}
                >
                  <SelectTrigger aria-label={`Role for ${label}`} aria-describedby={selectDescribed} className="h-8 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="outline" size="sm" aria-label={`Remove ${label}`}
                aria-describedby={removeReason ? reasonId : undefined}
                disabled={isOwner || lastWithOthers}
                onClick={() => setRemoving(m)}
              >
                Remove
              </Button>
              {(removeReason ?? roleReason) && (
                <span id={reasonId} className="text-xs text-muted-foreground">{removeReason ?? roleReason}</span>
              )}
            </li>
          );
        })}
        {members.length === 0 && <li className="px-3 py-4 text-sm text-muted-foreground">No members yet.</li>}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline" disabled={candidates.length === 0}
          aria-describedby={candidates.length === 0 ? 'no-transfer-reason' : undefined}
          onClick={() => setTransferOpen(true)}
        >
          Transfer ownership
        </Button>
        {candidates.length === 0 && (
          <span id="no-transfer-reason" className="text-xs text-muted-foreground">No other members to transfer to</span>
        )}
      </div>

      <Confirm
        open={removing !== null}
        title="Remove member?"
        text={removing ? `${removing.email ?? removing.user_id} loses access to ${workspace.name}.` : ''}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.user_id)}
      />

      <Dialog open={transferOpen} onOpenChange={(next) => { if (!next) { setTransferOpen(false); setNewOwnerId(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
            <DialogDescription>A workspace has one owner. The previous owner becomes an admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={newOwnerId} onValueChange={setNewOwnerId}>
              <SelectTrigger aria-label="New owner"><SelectValue placeholder="Pick the new owner" /></SelectTrigger>
              <SelectContent>
                {candidates.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.email ?? m.user_id}</SelectItem>)}
              </SelectContent>
            </Select>
            {sentence && <p className="text-sm">{sentence}</p>}
            {inactive.map((m) => (
              <p key={m.user_id} className="text-xs text-muted-foreground">
                {`Not available (inactive account): ${m.email ?? m.user_id}`}
              </p>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setTransferOpen(false); setNewOwnerId(''); }}>Cancel</Button>
            <Button onClick={() => newOwner && transfer.mutate(newOwner.user_id)} disabled={!newOwner || transfer.isPending}>
              Confirm transfer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invitations (read-only)
// ---------------------------------------------------------------------------

function InvitationsTab({ workspace }: { workspace: AdminWorkspace }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'workspace-invitations', workspace.id],
    queryFn: () => listInvitations(workspace.id),
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading invitations…</p>;
  if (isError) return <p className="text-sm text-destructive">Could not load the invitations.</p>;
  const pending = (data ?? []).filter((i) => i.status === 'pending');
  return (
    <div className="space-y-3">
      {pending.length === 0 ? (
        <p className="text-sm text-muted-foreground">No pending invitations.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border text-sm">
          {pending.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="truncate">{i.email}</span>
              <span className="text-xs text-muted-foreground">{i.role}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Invitations are sent and managed on the workspace&apos;s Team page (Settings &gt; Team).
      </p>
    </div>
  );
}

export function WorkspaceManageDrawer({
  workspace, onClose,
}: { workspace: AdminWorkspace | null; onClose: () => void }) {
  const [staleFor, setStaleFor] = useState<string | null>(null);
  const noteStale: StaleNote = (result) => {
    if (result && result.cache_invalidated === false && workspace) setStaleFor(workspace.id);
  };
  return (
    <Sheet open={workspace !== null} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {workspace && (
          <>
            <SheetHeader>
              <SheetTitle>{workspace.name}</SheetTitle>
              <SheetDescription>Manage this workspace without switching to it.</SheetDescription>
            </SheetHeader>
            {staleFor === workspace.id && (
              <p role="status" className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                The change is saved, but the access cache could not be refreshed, so it can take up to 5 minutes to apply.
              </p>
            )}
            <Tabs defaultValue="details" className="mt-4">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="members">Members</TabsTrigger>
                <TabsTrigger value="invitations">Invitations</TabsTrigger>
              </TabsList>
              <TabsContent value="details"><DetailsTab key={workspace.id} workspace={workspace} onResult={noteStale} /></TabsContent>
              <TabsContent value="members"><MembersTab workspace={workspace} onResult={noteStale} /></TabsContent>
              <TabsContent value="invitations"><InvitationsTab workspace={workspace} /></TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default WorkspaceManageDrawer;
